import { useState, useEffect, useCallback } from 'react'
import { AdminAlert } from '../AdminShared'
import { Skeleton } from '../components/Skeleton'
import { adminGetAuditLogs } from '../../../services/admin'

const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'DELETE_POST', label: 'Delete Post' },
  { value: 'DELETE_COMMENT', label: 'Delete Comment' },
  { value: 'MUTE_USER', label: 'Mute User' },
  { value: 'UNMUTE_USER', label: 'Unmute User' },
  { value: 'CPT_ADD', label: 'CPT Added' },
  { value: 'CPT_REDUCE', label: 'CPT Reduced' },
  { value: 'CIS_ADD', label: 'CIS Added' },
  { value: 'CIS_SUBTRACT', label: 'CIS Subtracted' },
]

const ACTION_STYLE = {
  DELETE_POST:    'border-error text-error bg-error/10',
  DELETE_COMMENT: 'border-error text-error bg-error/10',
  MUTE_USER:      'border-error text-error bg-error/10',
  UNMUTE_USER:    'border-primary-container text-on-primary-container bg-primary-container/20',
  CPT_ADD:        'border-primary-container text-on-primary-container bg-primary-container/20',
  CPT_REDUCE:     'border-error text-error bg-error/10',
  CIS_ADD:        'border-primary-container text-on-primary-container bg-primary-container/20',
  CIS_SUBTRACT:   'border-error text-error bg-error/10',
}

function actionLabel(action) {
  return ACTION_OPTIONS.find((o) => o.value === action)?.label ?? action ?? 'Action'
}

function AuditLogRow({ entry }) {
  const style = ACTION_STYLE[entry.action] ?? 'border-on-background/30 text-on-surface-variant'
  return (
    <div className="border-2 border-on-background/15 bg-surface p-4 flex items-start justify-between gap-3 flex-wrap">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 border-2 font-bold text-[10px] uppercase tracking-widest ${style}`}>
            {actionLabel(entry.action)}
          </span>
          <span className="text-[12px] font-bold truncate">{entry.targetUsername ?? entry.targetUserId}</span>
          {entry.amount != null && (
            <span className="text-[12px] font-mono text-on-surface-variant">({entry.amount})</span>
          )}
        </div>
        {entry.reason && <p className="text-[12px] text-on-surface-variant mt-1">{entry.reason}</p>}
        <p className="text-[11px] text-on-surface-variant font-mono mt-1">By: {entry.moderatorEmail ?? entry.moderatorId}</p>
      </div>
      <span className="text-[11px] text-on-surface-variant font-mono shrink-0">
        {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : ''}
      </span>
    </div>
  )
}

export function AuditLogManagement() {
  const [moderatorId, setModeratorId] = useState('')
  const [targetUserId, setTargetUserId] = useState('')
  const [action, setAction] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const LIMIT = 25

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback((nextOffset = 0) => {
    setLoading(true)
    setError(null)
    adminGetAuditLogs({
      moderatorId: moderatorId.trim(),
      targetUserId: targetUserId.trim(),
      action,
      from,
      to,
      offset: nextOffset,
      limit: LIMIT,
    })
      .then((res) => {
        setRows(res.rows ?? [])
        setTotal(res.total ?? 0)
        setOffset(nextOffset)
      })
      .catch((err) => setError(err.message ?? 'Failed to load audit logs'))
      .finally(() => setLoading(false))
  }, [moderatorId, targetUserId, action, from, to])

  useEffect(() => { load(0) }, []) 

  const handleApplyFilters = (e) => {
    e.preventDefault()
    load(0)
  }

  const handleClearFilters = () => {
    setModeratorId('')
    setTargetUserId('')
    setAction('')
    setFrom('')
    setTo('')
    setTimeout(() => load(0), 0)
  }

  const hasMore = offset + rows.length < total
  const hasPrev = offset > 0

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="font-bold text-[16px]">Audit Logs</h2>
        <p className="text-[12px] text-on-surface-variant mt-1">
          Every admin-triggered action that changes user state — post/comment deletes, mutes, and CPT/CIS manual adjustments.
        </p>
      </div>

      <form onSubmit={handleApplyFilters} className="border-2 border-on-background bg-surface p-4 flex flex-col gap-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Moderator (Admin) ID</label>
            <input
              type="text"
              value={moderatorId}
              onChange={(e) => setModeratorId(e.target.value)}
              placeholder="paste admin user ID"
              className="bg-surface border-2 border-on-background/20 px-3 py-2 text-[12px] font-mono focus:outline-none focus:border-primary-container"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Target User ID</label>
            <input
              type="text"
              value={targetUserId}
              onChange={(e) => setTargetUserId(e.target.value)}
              placeholder="paste profile ID"
              className="bg-surface border-2 border-on-background/20 px-3 py-2 text-[12px] font-mono focus:outline-none focus:border-primary-container"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Action Type</label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="bg-surface border-2 border-on-background/20 px-3 py-2 text-[12px] font-bold focus:outline-none"
            >
              {ACTION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="bg-surface border-2 border-on-background/20 px-3 py-2 text-[12px] focus:outline-none focus:border-primary-container"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="bg-surface border-2 border-on-background/20 px-3 py-2 text-[12px] focus:outline-none focus:border-primary-container"
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button type="submit" className="px-4 py-2 bg-primary-container text-on-primary-fixed font-bold text-[11px] uppercase tracking-widest border-2 border-on-background hover:brightness-105 transition-all">
            Apply Filters
          </button>
          <button type="button" onClick={handleClearFilters} className="px-4 py-2 border-2 border-on-background/30 font-bold text-[11px] uppercase tracking-widest text-on-surface-variant hover:bg-surface-container transition-colors">
            Clear
          </button>
          {!loading && <span className="text-[12px] text-on-surface-variant ml-auto">{total} total entries</span>}
        </div>
      </form>

      {error && <AdminAlert type="error" message={error} />}

      <div className="flex flex-col gap-2">
        {loading ? (
          <>
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </>
        ) : rows.length === 0 ? (
          <div className="border-2 border-dashed border-on-background/25 p-8 text-center">
            <p className="font-bold text-[12px] uppercase tracking-widest text-on-surface-variant">No matching audit log entries</p>
          </div>
        ) : (
          rows.map((entry) => <AuditLogRow key={entry.id} entry={entry} />)
        )}
      </div>

      {!loading && rows.length > 0 && (
        <div className="flex items-center justify-between gap-3 pt-2">
          <button
            onClick={() => load(Math.max(0, offset - LIMIT))}
            disabled={!hasPrev}
            className="px-4 py-2 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest disabled:opacity-40 hover:bg-surface-container transition-colors"
          >
            ← Previous
          </button>
          <span className="text-[11px] text-on-surface-variant">
            Showing {offset + 1}–{offset + rows.length} of {total}
          </span>
          <button
            onClick={() => load(offset + LIMIT)}
            disabled={!hasMore}
            className="px-4 py-2 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest disabled:opacity-40 hover:bg-surface-container transition-colors"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  )
}